import {
    GetAllPagesDocument,
    GetAllPagesForSitemapDocument,
    GetAllStoriesDocument,
    GetBasicPageDocument,
    GetGlobalDocument,
    GetPageDocument,
    IPageEntity,
    IStoryEntity,
} from '@/graphql/__generated__/global/types';
import { I18nLocales } from '@/interfaces/locales.interfaces';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import client, { flattenResponse } from './apolloClient';

export const getSingularPage = async (client: ApolloClient<NormalizedCacheObject>, locale: I18nLocales, slug: string | undefined, preview?: boolean) => {
    const { data } = await client.query({
        query: GetPageDocument,
        variables: {
            locale: locale,
            slug: `${slug}`,
            publicationState: preview ? 'PREVIEW' : 'LIVE',
        },
    });
    return flattenResponse(data);
};

const stripSlashes = (str: string) => str.replace(/^\/|\/$/g, '');

export const findFullSlug = (page?: IPageEntity | IStoryEntity): string[] => {
    const paths: string[] = [];
    const parent = page?.attributes?.parentPage?.data;

    if (parent) {
        const parentPaths = findFullSlug(parent);
        parentPaths.forEach((parentPath) => {
            const parts = [parentPath, page?.attributes?.slug || ''].filter(Boolean).map(stripSlashes);
            paths.push(parts.join('/'));
        });
    } else {
        paths.push(stripSlashes(page?.attributes?.slug || ''));
    }

    return paths;
};

export const getAllPages = async (client: ApolloClient<NormalizedCacheObject>, locale: I18nLocales, preview?: boolean) => {
    const { data } = await client.query({
        query: GetAllPagesDocument,
        variables: {
            locale,
            publicationState: preview ? 'PREVIEW' : 'LIVE',
        },
    });

    const pages = data.pages.data as IPageEntity[];
    const stories = data.stories.data as IStoryEntity[];

    const paths: string[] = [];

    pages.forEach((page) => {
        const itemPaths = findFullSlug(page);
        itemPaths.forEach((itemPath) => {
            paths.push(itemPath);
        });
    });

    stories.forEach((story) => {
        const itemPaths = findFullSlug(story);
        itemPaths.forEach((itemPath) => {
            paths.push(itemPath);
        });
    });

    return paths;
};

export const getAllStories = async (locale: I18nLocales, preview?: boolean) => {
    const { data } = await client.query({
        query: GetAllStoriesDocument,
        variables: {
            locale,
            publicationState: preview ? 'PREVIEW' : 'LIVE',
        },
    });

    const stories = data.stories.data as IStoryEntity[];

    const mappedStories = stories.map((story) => ({
        id: story.id,
        attributes: {
            ...story.attributes,
            slug: findFullSlug(story).join('/'),
        },
    }));

    return mappedStories;
};

export const fetchGlobalData = async (locale: I18nLocales) => {
    const { data } = await client.query({
        query: GetGlobalDocument,
        variables: {
            locale: locale,
            country: (locale === I18nLocales.au && 'Australia') || (locale === I18nLocales.nz && 'New Zealand'),
        },
    });

    return data;
};

export const getAllPagesForSitemap = async (client: ApolloClient<NormalizedCacheObject>) => {
    const locales = [I18nLocales.nz, I18nLocales.au];
    const pages = [];

    for (const locale of locales) {
        const { data } = await client.query({
            query: GetAllPagesForSitemapDocument,
            variables: {
                locale: locale,
            },
        });

        pages.push(...(data.pages.data as IPageEntity[]));
        pages.push(...(data.stories.data as IStoryEntity[]));
    }

    return pages;
};

export const getDefaultPage = async (client: ApolloClient<NormalizedCacheObject>, locale: I18nLocales) => {
    const { data } = await client.query({
        query: GetBasicPageDocument,
        variables: {
            locale: locale,
        },
    });
    return flattenResponse(data);
};
